import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import styled from "styled-components";
import { HFF_MODULE_NAME_MAP } from "common/models/types";
import { COLOR_SYSTEM } from "components/design/design-system";
import { fetchDrugLabelByItemCode } from "api/drugService";
import { RootState } from "store";
import { setDrugLabels } from "store/actions";
import DrugLabelInfo from "./index";
import { Spinner } from "components/atoms";
import Text from "components/atoms/Text";

interface Props {
  itemCode: string;
  productName?: string;
}

// 로딩/빈/에러 상태를 패널 중앙에 배치 (보고서 분석 로딩 카드 패턴 참고)
const StatusWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  min-height: 360px;
  padding: 48px 16px;
`;

// 보고서 AnalysisStatus의 StatusIconContainer 패턴 — 원형 배경 위 스피너
const SpinnerCircle = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${COLOR_SYSTEM.get("Blue")?.[50]};
  display: flex;
  align-items: center;
  justify-content: center;
`;

/**
 * 슬라이드 패널에 들어가는 허가사항 콘텐츠.
 * 허가사항은 redux store(drugLabels[itemCode])에 캐시한다. 캐시가 있으면 즉시 표시하고,
 * 없을 때만(undefined) fetchDrugLabelByItemCode로 조회 후 store에 저장한다(같은 약 재조회 방지).
 * 건기식(HFF 접두사)은 DrugLabelInfo 내부 ID 생성과 동일하게 itemCode로 판정.
 */
const DrugLabelSlideContent: React.FC<Props> = ({ itemCode, productName }) => {
  const dispatch = useDispatch();
  // undefined = 미조회, [] = 조회했으나 없음, [...] = 캐시 히트
  const labels = useSelector((s: RootState) => s.data.drugLabels[itemCode]);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (labels !== undefined) return; // 캐시 히트 → 재조회 안 함
    let alive = true;
    setError(false);
    fetchDrugLabelByItemCode(itemCode)
      .then((data) => {
        if (alive) dispatch(setDrugLabels({ itemCode, labels: data }));
      })
      .catch(() => {
        if (alive) setError(true);
      });
    return () => {
      alive = false;
    };
  }, [itemCode, labels, dispatch]);

  if (error && labels === undefined)
    return (
      <StatusWrap>
        <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[600]}>
          허가사항을 불러오지 못했습니다.
        </Text>
      </StatusWrap>
    );
  if (!labels)
    return (
      <StatusWrap>
        <SpinnerCircle>
          <Spinner size="Medium" />
        </SpinnerCircle>
        <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[600]}>
          허가사항을 불러오는 중...
        </Text>
      </StatusWrap>
    );
  if (labels.length === 0)
    return (
      <StatusWrap>
        <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[600]}>
          표시할 허가사항이 없습니다.
        </Text>
      </StatusWrap>
    );

  const isHff = itemCode.startsWith("HFF");
  return (
    <DrugLabelInfo
      labelInfo={labels}
      itemCode={itemCode}
      productName={productName}
      showMetadata={false}
      moduleNameMap={isHff ? HFF_MODULE_NAME_MAP : undefined}
    />
  );
};

export default DrugLabelSlideContent;
